import Alert from '@mui/material/Alert';
import React from 'react';

import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  Filter,
  ReferenceField,
  SaveButton,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import AutocompleteClientInput from './components/AutocompleteClientInput';
import AutocompleteLocationInput from './components/AutocompleteLocationInput';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const FCM_ELIGIBLE_HELPER =
  'Members at this office must have an active MBO gym membership. Enable this + disable App Eligible to enforce the Join Gym paywall (users must join the gym before accessing the app).';

const APP_ELIGIBLE_HELPER =
  "Members at this office can access the app without a gym membership. Disable this (with FCM Eligible ON) to require gym join during onboarding — paywall mode. The eligibility file must include this office's Client Office ID for the paywall to trigger correctly.";

// Rendered inside a react-hook-form context (SimpleForm), so useWatch is safe here.
const PaywallModeAlert: React.FC = () => {
  const isFcmEligible = useWatch({ name: 'isFcmEligible' });
  const isAppEligible = useWatch({ name: 'isAppEligible' });
  if (!isFcmEligible || isAppEligible) {
    return null;
  }
  return (
    <Alert severity="warning" sx={{ mb: 1 }}>
      <strong>Paywall office flags set.</strong> Members at this office will be
      blocked until they join the gym, provided gym-membership eligibility is
      also enabled on the client. The eligibility file <strong>must</strong>{' '}
      include this office&apos;s Client Office ID.
    </Alert>
  );
};

const OfficeToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const OfficeFilters: React.FC = (props) => (
  <Filter {...props}>
    <AutocompleteClientInput source="clientId" label="Client" alwaysOn />
  </Filter>
);

export const OfficeList: React.FC = () => (
  <InfiniteList filters={<OfficeFilters />}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="name" label="Office Name" sortable={false} />
      <ReferenceField
        source="client.id"
        reference="Client"
        label="Client"
        sortable={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <TextField
        source="clientOfficeId"
        label="Client Office ID"
        sortable={false}
      />
      <BooleanField
        source="isFcmEligible"
        label="FCM Eligible"
        sortable={false}
      />
      <BooleanField
        source="isAppEligible"
        label="App Eligible"
        sortable={false}
      />
      <WriteOnlyEditButton />
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

const LocationInputWithClientFilter: React.FC = () => {
  const clientId = useWatch({ name: 'client.id' });
  const { setValue } = useFormContext();
  const prevClientIdRef = React.useRef(clientId);

  React.useEffect(() => {
    // Clear location when client changes
    if (prevClientIdRef.current !== clientId) {
      setValue('location.id', null);
      prevClientIdRef.current = clientId;
    }
  }, [clientId, setValue]);

  return (
    <AutocompleteLocationInput
      source="location.id"
      label="Location"
      clientId={clientId}
      isRequired={false}
    />
  );
};

const LocationInputWithRecord: React.FC = () => {
  const record = useRecordContext();
  const clientId = record?.client?.id;

  return (
    <AutocompleteLocationInput
      source="location.id"
      label="Location"
      clientId={clientId}
      isRequired={false}
    />
  );
};

export const OfficeShow: React.FC = () => {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="id" label="ID" />
        <TextField source="name" label="Office Name" />
        <ReferenceField source="client.id" reference="Client" label="Client">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          source="location.id"
          reference="CoachLocation"
          label="Location"
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField source="clientOfficeId" label="Client Office ID" />
        <BooleanField source="isFcmEligible" label="FCM Eligible" />
        <BooleanField source="isAppEligible" label="App Eligible" />
      </SimpleShowLayout>
    </Show>
  );
};

export const OfficeEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm toolbar={<OfficeToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <TextInput source="name" label="Office Name" />
      <AutocompleteClientInput source="client.id" label="Client" />
      <LocationInputWithRecord />
      <TextInput source="clientOfficeId" label="Client Office ID" required />
      <BooleanInput
        source="isFcmEligible"
        label="FCM Eligible"
        helperText={FCM_ELIGIBLE_HELPER}
      />
      <BooleanInput
        source="isAppEligible"
        label="App Eligible"
        helperText={APP_ELIGIBLE_HELPER}
      />
      <PaywallModeAlert />
    </SimpleForm>
  </Edit>
);

export const OfficeCreate: React.FC = () => (
  <Create>
    <SimpleForm>
      <AutocompleteClientInput source="client.id" label="Client" />
      <TextInput source="name" label="Office Name" />
      <LocationInputWithClientFilter />
      <TextInput source="clientOfficeId" label="Client Office ID" required />
      <BooleanInput
        source="isFcmEligible"
        label="FCM Eligible"
        helperText={FCM_ELIGIBLE_HELPER}
      />
      <BooleanInput
        source="isAppEligible"
        label="App Eligible"
        helperText={APP_ELIGIBLE_HELPER}
      />
      <PaywallModeAlert />
    </SimpleForm>
  </Create>
);
